import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  HouseLine,
  Stack,
  UsersThree,
  SignOut,
  List as ListIcon,
  Crown,
} from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import BrandLogo from "./BrandLogo";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { Key } from "@phosphor-icons/react";

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
    onNavigate?.();
  };

  return (
    <div className="flex flex-col h-full bg-white" data-testid="sidebar">
      <div className="px-5 py-6 border-b border-zinc-100">
        <BrandLogo variant="sidebar" />
      </div>

      <nav className="flex-1 p-3 space-y-1">
        <div className="label-eyebrow text-zinc-400 px-3 pt-2 pb-1">Workspace</div>
        <NavLink to="/dashboard" onClick={onNavigate}
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          data-testid="nav-dashboard">
          <HouseLine size={18} weight="bold" /> Dashboard
        </NavLink>
        <NavLink to="/projects" onClick={onNavigate}
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          data-testid="nav-projects">
          <Stack size={18} weight="bold" /> Projects
        </NavLink>
        {user && user.role !== "contractor" && (
          <NavLink to="/users" onClick={onNavigate}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            data-testid="nav-users">
            <UsersThree size={18} weight="bold" /> Users
          </NavLink>
        )}
        {user && user.role === "contractor" && (
          <NavLink to="/team" onClick={onNavigate}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            data-testid="nav-team">
            <UsersThree size={18} weight="bold" /> Team
          </NavLink>
        )}
        {user?.is_super_admin && (
          <NavLink to="/admin" onClick={onNavigate}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            data-testid="nav-admin">
            <Crown size={18} weight="fill" className="text-[#007BFF]" /> Admin Portal
          </NavLink>
        )}
      </nav>

      <div className="p-3 border-t border-zinc-100">
        <div className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-zinc-50 transition-colors">
          <div className="w-9 h-9 bg-gradient-to-br from-[#001E44] to-[#1E407C] rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#001E44] truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 truncate mt-0.5">
              {user?.role?.replace("_", " ")}
              {user?.company ? ` · ${user.company}` : ""}
            </div>
          </div>
          <ChangePasswordDialog>
            <button
              type="button"
              className="p-2 rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-[#001E44] transition-colors"
              data-testid="open-change-password"
              title="Change password"
              aria-label="Change password"
            >
              <Key size={18} weight="bold" />
            </button>
          </ChangePasswordDialog>
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-[#001E44] transition-colors"
            data-testid="logout-button"
            title="Sign out"
          >
            <SignOut size={18} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[#F3F5F7]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-zinc-100 flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white text-[#001E44] px-4 py-3 flex items-center justify-between border-b border-zinc-100">
        <Link to="/dashboard" className="flex items-center" data-testid="mobile-logo">
          <BrandLogo variant="compact" />
        </Link>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              className="p-2 rounded-lg hover:bg-zinc-100 transition-colors"
              data-testid="mobile-menu-button"
              aria-label="Open menu"
            >
              <ListIcon size={22} weight="bold" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="p-0 w-72 bg-white border-zinc-100"
            data-testid="mobile-sidebar"
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden pt-14 lg:pt-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">{children}</div>
      </main>
    </div>
  );
}
